/**
 * Reacts to a dead Tahiti session (any 401) by clearing local auth state and
 * redirecting to `/login` with a message explaining why. Registered once at
 * boot in `main.tsx` via {@link initSessionExpiryHandling}.
 *
 * Multi-tab: the redirecting tab writes a sentinel to localStorage so other
 * open tabs, which may have no pending request to notice the 401 themselves,
 * redirect too (mirrors the storage-event pattern used by the feature-flags
 * panel for cross-tab sync).
 */
import { resetAuthGuardCache } from '@/lib/auth-guard';
import { clearToken } from '@/lib/auth-service';
import { registerUnauthorizedHandler } from '@/lib/tahiti-client';
import type { AnyRouter } from '@tanstack/react-router';

const BROADCAST_KEY = 'tahiti_session_expired_at';

// Module-scoped: the SPA never reloads on this redirect, so this must be
// reset explicitly once the user is authenticated again (see login.tsx).
let redirecting = false;
let currentRouter: AnyRouter | null = null;
// initSessionExpiryHandling is meant to run once at boot; guard the `storage`
// listener so a stray re-init (or a test calling it per-case) doesn't stack
// duplicate listeners — the handler always reads the latest `currentRouter`.
let listenerAttached = false;

/** Clears the stuck-redirecting guard; call once a fresh login succeeds. */
export function resetSessionExpiry(): void {
  redirecting = false;
}

function safeSetItem(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // localStorage unavailable (private mode) — cross-tab sync degrades
    // gracefully to per-tab detection, which still works.
  }
}

function goToLogin(): void {
  if (!currentRouter) return;
  resetAuthGuardCache();
  // Already on /login (e.g. a background request 401s while the user sits on the
  // login page): don't navigate to self — it would set redirect=/login and reset
  // the page. The storage-listener path guards this too; guarding here covers the
  // direct 401 path symmetrically.
  if (window.location.pathname === '/login') return;
  void currentRouter.navigate({
    to: '/login',
    search: { redirect: `${window.location.pathname}${window.location.search}`, reason: 'expired' },
  });
}

/** Wires the 401 handler and the cross-tab listener. Call once at boot. */
export function initSessionExpiryHandling(router: AnyRouter): void {
  currentRouter = router;

  registerUnauthorizedHandler(() => {
    if (redirecting) return;
    redirecting = true;
    clearToken();
    safeSetItem(BROADCAST_KEY, String(Date.now()));
    goToLogin();
  });

  if (listenerAttached) return;
  listenerAttached = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== BROADCAST_KEY || event.newValue === null) return;
    if (redirecting || window.location.pathname === '/login') return;
    redirecting = true;
    goToLogin();
  });
}
